import React from "react";
import { motion } from "framer-motion";
import img from "../../assets/images/Founder-image/founder.jpeg";
import { Scale, Award, BookOpenText, Handshake } from "lucide-react"; // Icons for depth

const AboutPageSection = () => {
  // PREMIUM LEGAL PALETTE
  const colors = {
    bgObsidian: "#0a0a0a", // Deep Black
    bgCard: "#111111", // Subtle Grey-Black
    gold: "#c5a059", // Brushed Gold
    textBrown: "#a18a68", // Golden-Brown Muted
    textLight: "#ffffff",
  };

  return (
    <div className="bg-[#0a0a0a] font-serif overflow-hidden selection:bg-[#c5a059] selection:text-black">
      {/* --- 1. LUXURY HERO SECTION (Revised Spacing & Style) --- */}
      <section className="relative min-h-[95vh] flex items-center px-6 md:px-12 pt-32 pb-24 border-b border-white/5">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dark-matter.png')]"></div>
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-20 items-center relative z-10">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-4 mb-8">
              <span
                className="w-12 h-[1px]"
                style={{ backgroundColor: colors.gold }}
              ></span>
              <p
                className="uppercase tracking-[0.5em] text-[10px] font-bold font-sans"
                style={{ color: colors.gold }}
              >
                Since 1995 • Adv Akash Giri
              </p>
            </div>

            <h1 className="text-6xl md:text-8xl font-medium tracking-tight leading-[1.05] mb-12 text-white">
              Cultivating a <br />
              <span
                className="italic font-light opacity-80"
                style={{ color: colors.gold }}
              >
                Legacy
              </span>{" "}
              of Trust.
            </h1>

            <div
              className="space-y-6 max-w-2xl font-sans text-[16px] leading-relaxed font-light text-justify"
              style={{ color: colors.textBrown }}
            >
              <p>
                <strong className="font-bold text-white">Adv Akash Giri</strong>{" "}
                legal consultancy is dedicated to building enduring,
                professional relationships with clients, founded upon absolute
                trust, confidentiality, and sound technical methodology.
              </p>
              <p>
                Our philosophy ensures consistent quality across all specialized
                legal domains, ranging from human rights and strategic
                litigation to complex commercial disputes and civil matters,
                serving clients at all wealth tiers.
              </p>
              <p>
                Beyond advisory, we implement robust recurring services—estate
                planning, probate, and wealth restructuring—handling probate and
                contractual arrangements that address complex legal demands.
              </p>
            </div>

            <button className="mt-16 group flex items-center gap-3 px-10 py-5 bg-[#c5a059] text-black transition-all duration-300 uppercase tracking-[0.3em] text-[11px] font-bold font-sans hover:bg-white">
              Explore Our History
              <span className="w-6 h-[1px] bg-black group-hover:w-10 transition-all"></span>
            </button>
          </motion.div>

          {/* Right: Modern Framed Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute inset-0 border border-[#c5a059] translate-x-4 translate-y-4 -z-10 opacity-20"></div>
            <div className="relative aspect-[4/5] bg-[#141414] overflow-hidden group">
              <img
                src={img}
                alt="Law Firm Library"
                className="w-full h-full object-cover grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-1000"
              />
            </div>
            {/* Elegant Floating Badge */}
            <div
              className="absolute -bottom-10 -left-10 p-10 hidden md:block border border-white/5 shadow-2xl"
              style={{ backgroundColor: colors.bgObsidian }}
            >
              <Scale
                size={40}
                style={{ color: colors.gold }}
                className="mb-4"
              />
              <p className="text-white text-5xl font-medium tracking-tight">
                15+
              </p>
              <p
                style={{ color: colors.textBrown }}
                className="text-[10px] uppercase tracking-widest mt-1 font-sans"
              >
                Years of Jurisprudence
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- 2. ETHOS SECTION (Minimalist split, Black & Gold) --- */}
      <section className="py-32" style={{ backgroundColor: colors.bgObsidian }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-20 items-center">
          <div className="lg:col-span-5">
            <span
              className="text-[10px] uppercase tracking-[0.4em] font-bold font-sans block mb-5"
              style={{ color: colors.gold }}
            >
              Core Mandate
            </span>

            <h2 className="text-5xl font-medium text-white mb-8 tracking-tight leading-tight">
              Foundations <br /> Of Excellence
            </h2>

            <p
              className="font-sans text-lg font-light max-w-md leading-relaxed"
              style={{ color: colors.textBrown }}
            >
              Every mandate is executed with precise attention to detail and
              unwavering dedication to favorable outcomes.
            </p>

            <div
              className="w-20 h-[1px] mt-12"
              style={{ backgroundColor: colors.gold }}
            ></div>
          </div>

          {/* UPDATED CARDS */}
          <div className="lg:col-span-7 grid md:grid-cols-2 gap-x-12 gap-y-16">
            {[
              {
                title: "Trust",
                desc: "Absolute confidentiality and transparent methodology form the bedrock of client relations.",
              },
              {
                title: "Precision",
                desc: "Strategic thinking ensures complex legal structures are handled with meticulously.",
              },
              {
                title: "Tradition",
                desc: "Honoring classical jurisprudence while integrating contemporary innovation.",
              },
              {
                title: "Integrity",
                desc: "Upholding the highest ethical standards in every legal challenge presented.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="pl-8 border-l border-white/20 group transition-all duration-500 hover:border-white hover:translate-x-2"
              >
                <h4
                  style={{ color: colors.gold }}
                  className="text-2xl font-medium mb-5 transition-all duration-500 group-hover:text-white"
                >
                  {item.title}
                </h4>

                <p
                  className="font-sans font-light text-sm leading-loose transition-all duration-500"
                  style={{ color: colors.textBrown }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* --- 3. THE FOUNDER'S STATEMENT (Focus on Adv. Akash Giri) --- */}
      <section className="py-40 px-6 relative border-t border-b border-white/5 bg-[#111]">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <QuoteIcon
            className="absolute -top-16 left-1/2 -translate-x-1/2 w-20 h-20 opacity-10"
            color={colors.gold}
          />

          <h2
            className="text-4xl md:text-5xl italic font-light leading-[1.6] mb-16 tracking-tight"
            style={{ color: colors.textBrown }}
          >
            "The essence of the law lies not just in words, but in ensuring
            justice remains an accessible fortress for the weak and a persistent
            check on power. We build that fortress daily."
          </h2>

          <div className="w-16 h-[1px] bg-[#c5a059] mx-auto mb-8"></div>

          <p className="uppercase tracking-[0.5em] text-sm font-medium font-sans text-white mb-2">
            Adv. Akash Giri
          </p>
          <p className="text-[10px] uppercase tracking-[0.3em] font-sans text-gray-600">
            Supreme Court of India • Founding Strategist
          </p>
        </div>
      </section>

      {/* --- 4. STATS SECTION (Dark Luxury Cards) --- */}
      <section className="py-24" style={{ backgroundColor: colors.bgObsidian }}>
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-12">
          {[
            { label: "High Court Matters", value: "2.8K+", icon: Scale },
            { label: "Associate Attorneys", value: "15+", icon: Handshake },
            { label: "High-Net patrons", value: "900+", icon: Award },
            { label: "Cases Filed", value: "14K+", icon: BookOpenText },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={i}
                className="p-10 border border-white/5 text-center bg-[#111] shadow-xl relative group hover:border-[#c5a059]/30 transition-all duration-500"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
              >
                <Icon
                  size={28}
                  style={{ color: colors.gold }}
                  className="mx-auto mb-6 opacity-30 group-hover:opacity-100 transition-opacity"
                />
                <p className="text-5xl lg:text-6xl font-medium text-white mb-3 tracking-tighter">
                  {stat.value}
                </p>
                <p
                  style={{ color: colors.textBrown }}
                  className="text-[10px] uppercase tracking-[0.3em] font-bold font-sans"
                >
                  {stat.label}
                </p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

// Helper Icon Component
const QuoteIcon = ({ className, color }) => (
  <svg
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    x="0px"
    y="0px"
    viewBox="0 0 100 100"
    className={className}
    fill={color}
  >
    <g>
      <path d="M19.1,65.8c0-8.9,1.7-16.9,5.2-24.1c3.5-7.1,8.3-13.6,14.6-19.3l7.9,5.7c-4.9,4.4-8.8,9.2-11.7,14.4c-2.9,5.1-4.3,10.6-4.3,16.5c2.3-1,4.7-1.6,7.2-1.6c5.7,0,10.4,1.8,14.2,5.5c3.8,3.7,5.7,8.3,5.7,13.8c0,5.9-2.1,10.8-6.3,14.8c-4.2,3.9-9.3,5.9-15.3,5.9c-6,0-10.9-2.1-14.8-6.3C19.9,76.9,19.1,71.8,19.1,65.8z M56.9,65.8c0-8.9,1.7-16.9,5.2-24.1c3.5-7.1,8.3-13.6,14.6-19.3l7.9,5.7c-4.9,4.4-8.8,9.2-11.7,14.4c-2.9,5.1-4.3,10.6-4.3,16.5c2.3-1,4.7-1.6,7.2-1.6c5.7,0,10.4,1.8,14.2,5.5c3.8,3.7,5.7,8.3,5.7,13.8c0,5.9-2.1,10.8-6.3,14.8c-4.2,3.9-9.3,5.9-15.3,5.9c-6,0-10.9-2.1-14.8-6.3C57.7,76.9,56.9,71.8,56.9,65.8z" />
    </g>
  </svg>
);

export default AboutPageSection;
